import React, { useMemo, useState } from 'react'
import styled from 'styled-components'
import { PieChart } from 'react-minimal-pie-chart';
import { flatten, pathOr, propOr, values as ramdaValues } from 'ramda'
import { ClickAwayListener, Tooltip, useMediaQuery } from '@mui/material'
import { useSelector } from 'react-redux'
import { getDashboardStats } from 'features/dashboard/ducks/selectors'
import { RAMP } from 'features/dashboard/charts/scoreColour'
import { radius, font, bp, shadow, cardStyle } from 'features/dashboard/tokens'

const TemplateBox = ({ template }) => {
  const dashboardStats = useSelector(getDashboardStats)
  const isMobile = useMediaQuery(`(max-width: ${bp.mobile})`)
  const [mobileTooltipOpen, setMobileTooltipOpen] = useState(false)

  const total = useMemo(() => {
    return flatten(ramdaValues(propOr([], 'percentageValues', template))).length
  }, [template])

  const completed = useMemo(() => {
    return pathOr([], ['percentageValues', 5], template).length
  }, [template])

  const bpsCount = useMemo(() => {
    return {
      1: pathOr([], ['percentageValues', 1], template).length,
      2: pathOr([], ['percentageValues', 2], template).length,
      3: pathOr([], ['percentageValues', 3], template).length,
      4: pathOr([], ['percentageValues', 4], template).length,
      5: pathOr([], ['percentageValues', 5], template).length
    }
  }, [template])

  const values = useMemo(() => {
    return [
      {
        title: `100% - ${bpsCount[5]}`,
        value: pathOr([], ['percentageValues', 5], template).length,
        color: RAMP[4]
      },
      {
        title: `75 - 99% - ${bpsCount[4]}`,
        value: pathOr([], ['percentageValues', 4], template).length,
        color: RAMP[3]
      },
      {
        title: `50 - 74% - ${bpsCount[3]}`,
        value: pathOr([], ['percentageValues', 3], template).length,
        color: RAMP[2]
      },
      {
        title: `20 - 49% - ${bpsCount[2]}`,
        value: pathOr([], ['percentageValues', 2], template).length,
        color: RAMP[1]
      },
      {
        title: `0 - 19% - ${bpsCount[1]}`,
        value: pathOr([], ['percentageValues', 1], template).length,
        color: RAMP[0]
      }
    ]
  }, [template])

  const tooltipText = `This template is applied to ${total} of your ${dashboardStats.activeBuildingPassportsCount} live Building Passports. ${completed} of these have 100% of the required information`

  if (isMobile) {
    return (
      <TemplateBoxWrapper>
        <TemplateName>{template.name}</TemplateName>
        <ClickAwayListener onClickAway={() => setMobileTooltipOpen(false)}>
          <InfoIconWrapper>
            <Tooltip
              placement='top'
              open={mobileTooltipOpen}
              disableHoverListener
              disableTouchListener
              disableFocusListener
              title={tooltipText}
            >
              <InfoIcon onClick={() => setMobileTooltipOpen(prev => !prev)}>i</InfoIcon>
            </Tooltip>
          </InfoIconWrapper>
        </ClickAwayListener>
        <BoxContent>
          <BoxChart>
            <PieChart
              animate
              lineWidth={15}
              data={values}
            />
            <ProgressCount>
              <CompletedCount>{completed}/{total}</CompletedCount>
              <div>complete</div>
            </ProgressCount>
          </BoxChart>
        </BoxContent>
      </TemplateBoxWrapper>
    )
  }

  return (
    <Tooltip
      placement='top'
      title={tooltipText}
    >
      <TemplateBoxWrapper>
        <TemplateName>{template.name}</TemplateName>
        <BoxContent>
          <BoxChart>
            <PieChart
              animate
              lineWidth={15}
              data={values}
            />
            <ProgressCount>
              <CompletedCount>{completed}/{total}</CompletedCount>
              <div>complete</div>
            </ProgressCount>
          </BoxChart>
        </BoxContent>
      </TemplateBoxWrapper>
    </Tooltip>
  )
}

export default TemplateBox

const TemplateBoxWrapper = styled.div`
  position: relative;
  box-sizing: border-box;
  min-width: 220px;
  max-width: 220px;
  padding: 15px 25px;
  height: 210px;
  ${cardStyle}
  margin-bottom: 20px;
  margin-right: 20px;

  @media (max-width: ${bp.mobile}) {
    height: 150px;
    min-height: 120px;
    padding: 12px 18px;
    max-width: 100%;
  }
`

const TemplateName = styled.div`
  width: 170px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  font-size: ${font.body};
  margin-bottom: 10px;
  color: ${({ theme }) => theme.colors.darkGrey};
`

const BoxContent = styled.div`
  display: flex;
  align-items: center;
  height: 150px;

  @media (max-width: ${bp.mobile}) {
    height: 110px;
  }
`

const BoxChart = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  height: 120px;
  position: relative;

  @media (max-width: ${bp.mobile}) {
    height: 88px;
  }
`

const ProgressCount = styled.div`
  position: absolute;
  top: 0;
  right: 0;
  bottom: 0;
  left: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: ${font.body};
  color: ${({ theme }) => theme.colors.darkGrey};
  flex-direction: column;
`

const CompletedCount = styled.div`
  font-size: ${font.title};
  font-weight: bold;
`

const InfoIconWrapper = styled.div`
  position: absolute;
  top: 10px;
  right: 10px;
  z-index: 1;
`

const InfoIcon = styled.span`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 22px;
  height: 22px;
  border-radius: ${radius.round};
  background-color: ${({ theme }) => theme.colors.primary};
  color: #fff;
  font-size: ${font.body};
  font-weight: 700;
  font-style: italic;
  font-family: Georgia, serif;
  cursor: pointer;
  user-select: none;
  box-shadow: ${shadow.card};
`
